import { useState } from 'react';
import { Mission } from '../../data/missions';
import { Button } from '@/components/ui/button';
import FileDrop from '../FileDrop/FileDrop';
import MissionShell, { ErrorNotice } from '../MissionShell/MissionShell';
import {
  downloadBlob,
  prepareScoreFiles,
  PreparedScoreFiles,
  scoreTrainee,
  trainerTemplateUrl,
  TrainerScoringSummary,
} from '../../api/batch9Api';
import { downloadWithAuth } from '../../api/authInterceptor';
import { useFileRun } from '../../hooks/useFileRun';
import type { FileResult } from '../../api/files';
import { ProcessingNotice } from '@/components/ui/processing-notice';

const STEPS = [
  'Download the template; the trainer fills in the question numbers, Particulars, Amounts (the key) and Marks allotted.',
  'Upload the filled template. A unique Index (e.g. 2.a) is added and you get a Trainer file and a Trainee file (no Amounts or marks).',
  'The trainee fills "AS per Trainee" in their file. Upload the Trainer file and the filled Trainee file to get the scored workbook; questions are matched by Index.',
];

interface PanelProps {
  mission: Mission;
  onBack: () => void;
}

function StepHeading({ n, title, color }: { n: number; title: string; color: string }) {
  return (
    <div className="mb-3 flex items-center gap-2">
      <span
        className="flex h-6 w-6 items-center justify-center rounded-full text-xs font-bold text-white"
        style={{ backgroundColor: color }}
      >
        {n}
      </span>
      <h3 className="text-sm font-semibold text-foreground">{title}</h3>
    </div>
  );
}

function Notes({ notes }: { notes: string[] }) {
  return (
    <>
      {notes.map((note) => (
        <div key={note} className="rounded-lg bg-amber-50 px-4 py-3 text-amber-800">{note}</div>
      ))}
    </>
  );
}

function TrainerScoringPanel({ mission, onBack }: PanelProps) {
  const [filled, setFilled] = useState<File[]>([]);
  const [trainer, setTrainer] = useState<File[]>([]);
  const [trainee, setTrainee] = useState<File[]>([]);
  const [prepared, setPrepared] = useState<PreparedScoreFiles | null>(null);
  const [prepareStatus, setPrepareStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [prepareError, setPrepareError] = useState<string | null>(null);
  const score = useFileRun<FileResult<TrainerScoringSummary>>();

  const handlePrepare = async () => {
    if (filled.length === 0) {
      setPrepareError('Upload the filled template.');
      setPrepareStatus('error');
      return;
    }
    setPrepareStatus('loading');
    setPrepareError(null);
    setPrepared(null);
    try {
      setPrepared(await prepareScoreFiles(filled[0]));
      setPrepareStatus('success');
    } catch (err) {
      setPrepareError(err instanceof Error ? err.message : 'Could not prepare the files.');
      setPrepareStatus('error');
    }
  };

  const handleScore = async () => {
    if (trainer.length === 0 || trainee.length === 0) {
      score.fail('Upload both the Trainer file and the Trainee file.');
      return;
    }
    await score.run(() => scoreTrainee(trainer[0], trainee[0]), 'Scoring failed.');
  };

  const p = prepared?.summary;
  const s = score.result?.summary;
  const section = 'mb-6 rounded-lg border border-border p-4';

  return (
    <MissionShell mission={mission} onBack={onBack} steps={STEPS} wide>
      <div className={section}>
        <StepHeading n={1} title="Download the template" color={mission.color} />
        <p className="text-sm text-muted-foreground">
          All sheets with their headers. The trainer enters the section number in Sr. No., the sub-question (a, b, c…) beside it,
          the Particulars, the correct Amount and the Marks allotted. Critical Level is a free column.
        </p>
        <button
          type="button"
          onClick={() =>
            downloadWithAuth(trainerTemplateUrl, 'Trainer_Score_Sheet_Template.xlsx').catch(() =>
              window.alert('Could not download the template. Try signing in again.'),
            )
          }
          className="mt-2 inline-block rounded-md border border-input bg-background px-3 py-1.5 text-sm font-medium text-foreground hover:bg-muted"
        >
          📥 Download template
        </button>
      </div>

      <div className={section}>
        <StepHeading n={2} title="Upload the filled template to create the Trainer and Trainee files" color={mission.color} />
        <p className="mb-3 text-sm text-muted-foreground">
          Each question gets a unique Index (section + sub-question, e.g. 2.a) in a new column after Sr. No. The Trainee file has
          the Amounts, Marks allotted and Marks Obtained removed, so the trainee sees no answers.
        </p>
        <FileDrop label="Filled template" required accept={mission.accept} hint={mission.maxSizeLabel} accentColor={mission.color} files={filled} onFiles={setFilled} />
        <Button
          onClick={handlePrepare}
          disabled={prepareStatus === 'loading'}
          style={{ backgroundColor: mission.color }}
          className="w-full text-white hover:opacity-90"
        >
          {prepareStatus === 'loading' ? 'Preparing…' : 'Generate Trainer & Trainee files'}
        </Button>
        {prepareStatus === 'loading' && <ProcessingNotice label={'Preparing…'} />}
        {prepareStatus === 'error' && prepareError && <ErrorNotice message={prepareError} />}
        <div className="mt-4 space-y-3 text-sm">
          {prepareStatus === 'success' && prepared && (
            <div className="rounded-lg bg-green-50 px-4 py-3 text-green-700">
              ✅ {prepared.summary.total_questions} questions indexed. Download the two files below.
            </div>
          )}
          <div className="grid gap-3 sm:grid-cols-2">
            {(
              [
                ['Trainer file', 'With the answers (Amounts) and Marks allotted. Keep this one.', prepared?.trainer],
                ['Trainee file', 'Amounts and marks removed. Give this one to the trainee.', prepared?.trainee],
              ] as const
            ).map(([label, hint, file]) => (
              <div key={label} className="rounded-lg border border-border bg-muted p-3">
                <div className="font-semibold text-foreground">{label}</div>
                <div className="mb-2 text-xs text-muted-foreground">{hint}</div>
                <button
                  type="button"
                  disabled={!file}
                  onClick={() => file && downloadBlob(file.blob, file.filename)}
                  className="rounded-md border border-input bg-background px-3 py-1.5 text-sm font-medium text-foreground hover:bg-background/70 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  📥 Download {label}
                </button>
              </div>
            ))}
          </div>
          {prepared && <Notes notes={prepared.summary.notes} />}
        </div>
      </div>

      <div className={section}>
        <StepHeading n={3} title="Upload the Trainer file and the filled Trainee file" color={mission.color} />
        <div className="grid gap-4 sm:grid-cols-2">
          <FileDrop label="Trainer file" required accept={mission.accept} hint={mission.maxSizeLabel} accentColor={mission.color} files={trainer} onFiles={setTrainer} />
          <FileDrop label="Trainee file (filled)" required accept={mission.accept} hint={mission.maxSizeLabel} accentColor={mission.color} files={trainee} onFiles={setTrainee} />
        </div>
        <Button
          onClick={handleScore}
          disabled={score.status === 'loading'}
          style={{ backgroundColor: mission.color }}
          className="w-full text-white hover:opacity-90"
        >
          {score.status === 'loading' ? 'Scoring…' : 'Generate Score Sheet'}
        </Button>
        {score.status === 'loading' && <ProcessingNotice label={'Scoring…'} />}
        {score.status === 'error' && score.error && <ErrorNotice message={score.error} />}
        {score.status === 'success' && score.result && (
          <div className="mt-4 space-y-3 text-sm">
            <div className="rounded-lg bg-green-50 px-4 py-3 text-green-700">✅ {score.result.filename} downloaded.</div>
            {s && (
              <>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    ['Sheets scored', s.sheets.length],
                    ['Total marks', `${s.total_marks} / ${s.maximum_marks}`],
                    ['Percentage', `${s.percentage}%`],
                  ].map(([label, value]) => (
                    <div key={label} className="rounded-lg border border-border bg-muted px-3 py-2 text-center">
                      <div className="text-lg font-bold text-foreground">{value}</div>
                      <div className="text-xs text-muted-foreground">{label}</div>
                    </div>
                  ))}
                </div>
                <div className="overflow-x-auto rounded-lg border border-border">
                  <table className="w-full text-left text-xs">
                    <thead className="bg-muted text-muted-foreground">
                      <tr>
                        {['Sheet', 'Correct', 'Wrong', 'Not answered', 'Manual', 'Marks', '%'].map((h) => (
                          <th key={h} className="px-3 py-2 font-semibold">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {s.sheets.map((row) => (
                        <tr key={row.sheet} className="border-t border-border text-foreground">
                          <td className="px-3 py-2">{row.sheet}</td>
                          <td className="px-3 py-2">{row.correct}</td>
                          <td className="px-3 py-2">{row.wrong}</td>
                          <td className="px-3 py-2">{row.unanswered}</td>
                          <td className="px-3 py-2">{row.manual}</td>
                          <td className="px-3 py-2">{row.marks_obtained} / {row.marks_allotted}</td>
                          <td className="px-3 py-2">{row.percentage}%</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {s.manual_review_rows > 0 && (
                  <div className="rounded-lg bg-amber-50 px-4 py-3 text-amber-800">
                    {s.manual_review_rows} row(s) have no answer key and are highlighted amber for manual marking.
                  </div>
                )}
                <Notes notes={s.notes} />
              </>
            )}
          </div>
        )}
      </div>
    </MissionShell>
  );
}

export default TrainerScoringPanel;
